// src/app/modules/salles/SalleManagement.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import {
    fetchSallesRequest,
    createSalleRequest,
    updateSalleRequest,
    deleteSalleRequest,
} from "../../../../redux/actions/sallesActions";

import "../RessourcesStyles/SalleManagement.css";

const emptyForm = {
    nom: "",
    code: "",
    type: "PHYSIQUE", // default
    color: "",
    description: "",
    maxPlaces: 0,
    tools: "",
};

export default function SalleManagement() {
    const dispatch = useDispatch();

    const { salles = [], loading } = useSelector((state) => state.salles || {});

    const [activeTab, setActiveTab] = useState("ALL"); // ALL | PHYSIQUE | VIRTUELLE
    const [search, setSearch] = useState("");

    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        dispatch(fetchSallesRequest());
    }, [dispatch]);

    const normalizedType = (t) => String(t || "").trim().toUpperCase();

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();

        return (salles || []).filter((s) => {
            const t = normalizedType(s.type);
            const okTab =
                activeTab === "ALL" ? true : t === activeTab;

            const name = String(s.nom || "").toLowerCase();
            const code = String(s.code || "").toLowerCase();
            const okSearch = !q || name.includes(q) || code.includes(q);

            return okTab && okSearch;
        });
    }, [salles, activeTab, search]);

    const openAdd = () => {
        setIsEdit(false);
        setCurrentId(null);
        setForm(emptyForm);
        setOpen(true);
    };

    const openEdit = (s) => {
        setIsEdit(true);
        setCurrentId(s.id);
        setForm({
            nom: s.nom || "",
            code: s.code || "",
            type: normalizedType(s.type) || "PHYSIQUE",
            color: s.color || "",
            description: s.description || "",
            maxPlaces: s.maxPlaces ?? 0,
            tools: s.tools || "",
        });
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setForm(emptyForm);
    };

    const onSubmit = () => {
        if (!form.code?.trim()) {
            Swal.fire({ icon: "warning", text: "Code obligatoire", timer: 1800, showConfirmButton: false });
            return;
        }
        if (!form.nom?.trim()) {
            Swal.fire({ icon: "warning", text: "Nom obligatoire", timer: 1800, showConfirmButton: false });
            return;
        }
        if (!form.type?.trim()) {
            Swal.fire({ icon: "warning", text: "Type obligatoire", timer: 1800, showConfirmButton: false });
            return;
        }

        const payload = {
            nom: form.nom,
            code: form.code,
            type: form.type,
            color: form.color,
            description: form.description,
            maxPlaces: Number(form.maxPlaces || 0),
            tools: form.tools,
        };

        if (isEdit && currentId) {
            // IMPORTANT: ton style redux = updateSalleRequest(id, payload)
            dispatch(updateSalleRequest(currentId, payload));
        } else {
            dispatch(createSalleRequest(payload));
        }
        closeModal();
    };

    const onDelete = (id) => {
        Swal.fire({
            title: "Supprimer ?",
            text: "Cette action est irréversible",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Annuler",
        }).then((res) => {
            if (res.isConfirmed) dispatch(deleteSalleRequest(id));
        });
    };

    return (
        <div className="salle-page">
            <div className="salle-header">
                <div>
                    <h2>Salles</h2>
                    <div className="salle-subtitle">Gestion des salles par type (Physique / Virtuelle)</div>
                </div>

                <button className="salle-btn salle-btn-primary" onClick={openAdd}>
                    + Ajouter Salle
                </button>
            </div>

            {/* Tabs */}
            <div className="salle-tabs">
                <button
                    className={`salle-tab ${activeTab === "ALL" ? "active" : ""}`}
                    onClick={() => setActiveTab("ALL")}
                >
                    Toutes
                </button>
                <button
                    className={`salle-tab ${activeTab === "PHYSIQUE" ? "active" : ""}`}
                    onClick={() => setActiveTab("PHYSIQUE")}
                >
                    Physiques
                </button>
                <button
                    className={`salle-tab ${activeTab === "VIRTUELLE" ? "active" : ""}`}
                    onClick={() => setActiveTab("VIRTUELLE")}
                >
                    Virtuelles
                </button>

                <div className="salle-tabs-spacer" />

                <input
                    className="salle-search"
                    placeholder="Rechercher (nom / code)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div className="salle-card">
                <div className="salle-table-wrap">
                    <table className="salle-table">
                        <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Code</th>
                            <th>Type</th>
                            <th>Capacité</th>
                            <th>Couleur</th>
                            <th style={{ width: 170 }}>Actions</th>
                        </tr>
                        </thead>

                        <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={6} style={{ textAlign: "center", padding: 18, opacity: 0.7 }}>
                                    Aucune salle
                                </td>
                            </tr>
                        ) : (
                            filtered.map((s) => (
                                <tr key={s.id}>
                                    <td>{s.nom}</td>
                                    <td className="salle-badge">{s.code}</td>
                                    <td>
                      <span className={`salle-pill ${normalizedType(s.type) === "VIRTUELLE" ? "virt" : "phys"}`}>
                        {normalizedType(s.type) === "VIRTUELLE" ? "Virtuelle" : "Physique"}
                      </span>
                                    </td>
                                    <td>{s.maxPlaces ?? "-"}</td>
                                    <td>
                                        {s.color ? (
                                            <span className="salle-color">
                          <span className="salle-color-dot" style={{ background: s.color }} />
                                                {s.color}
                        </span>
                                        ) : (
                                            "-"
                                        )}
                                    </td>

                                    <td>
                                        <div className="salle-actions">
                                            <button className="salle-btn salle-btn-light" onClick={() => openEdit(s)}>
                                                Modifier
                                            </button>
                                            <button className="salle-btn salle-btn-danger" onClick={() => onDelete(s.id)}>
                                                Supprimer
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>

                    {loading && <div className="salle-loading">Chargement...</div>}
                </div>
            </div>

            {/* Modal */}
            {open && (
                <div className="salle-modal" role="dialog" aria-modal="true" onMouseDown={closeModal}>
                    <div className="salle-modal-panel" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="salle-modal-head">
                            <h3>{isEdit ? "Modifier Salle" : "Ajouter Salle"}</h3>
                            <button className="salle-icon-btn" onClick={closeModal}>✕</button>
                        </div>

                        <div className="salle-grid">
                            <div className="salle-field">
                                <label>Nom *</label>
                                <input
                                    value={form.nom}
                                    onChange={(e) => setForm({ ...form, nom: e.target.value })}
                                />
                            </div>

                            <div className="salle-field">
                                <label>Code *</label>
                                <input
                                    value={form.code}
                                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                                />
                            </div>

                            <div className="salle-field">
                                <label>Type *</label>
                                <select
                                    value={form.type}
                                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                                >
                                    <option value="PHYSIQUE">Physique</option>
                                    <option value="VIRTUELLE">Virtuelle</option>
                                </select>
                            </div>

                            <div className="salle-field">
                                <label>Capacité *</label>
                                <input
                                    type="number"
                                    min={0}
                                    value={form.maxPlaces}
                                    onChange={(e) => setForm({ ...form, maxPlaces: e.target.value })}
                                />
                            </div>

                            <div className="salle-field">
                                <label>Couleur</label>
                                <input
                                    placeholder="#204385"
                                    value={form.color}
                                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                                />
                            </div>

                            <div className="salle-field salle-field-full">
                                <label>Outils / Équipements</label>
                                <input
                                    placeholder="Projecteur, Tableau, Teams, Zoom..."
                                    value={form.tools}
                                    onChange={(e) => setForm({ ...form, tools: e.target.value })}
                                />
                            </div>

                            <div className="salle-field salle-field-full">
                                <label>Description</label>
                                <textarea
                                    rows={4}
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="salle-modal-foot">
                            <button className="salle-btn salle-btn-light" onClick={closeModal}>
                                Annuler
                            </button>
                            <button className="salle-btn salle-btn-primary" onClick={onSubmit}>
                                {isEdit ? "Enregistrer" : "Créer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
